import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';

import { AtmOperationType, AtmTransaction } from '../../types/atm';

interface AtmResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: AtmTransaction | null;
  onViewReceipt?: () => void;
  onTransactAgain?: () => void;
}

const AtmResultModal: React.FC<AtmResultModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onViewReceipt,
  onTransactAgain,
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Update window size for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show confetti when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Stop confetti after 5 seconds
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Get emoji based on transaction type
  const getTransactionEmoji = (type: AtmOperationType) => {
    switch (type) {
      case AtmOperationType.DEPOSIT:
        return '💸';
      case AtmOperationType.WITHDRAWAL:
        return '💰';
      case AtmOperationType.BALANCE:
        return '📊';
      case AtmOperationType.TRANSFER:
        return '💳';
      case AtmOperationType.CUSTOM:
        return '🎯';
      default:
        return '✅';
    }
  };

  // Get celebration text based on transaction type
  const getCelebrationText = (type: AtmOperationType) => {
    switch (type) {
      case AtmOperationType.DEPOSIT:
        return 'Deposit Complete!';
      case AtmOperationType.WITHDRAWAL:
        return 'Withdrawal Complete!';
      case AtmOperationType.BALANCE:
        return 'Balance Retrieved!';
      case AtmOperationType.TRANSFER:
        return 'Transfer Complete!';
      case AtmOperationType.CUSTOM:
        return 'Transaction Complete!';
      default:
        return 'Success!';
    }
  };

  // Handle close
  const handleClose = () => {
    onClose();
  };

  // Handle view receipt
  const handleViewReceipt = () => {
    if (onViewReceipt) {
      onViewReceipt();
    }
    onClose();
  };

  // Handle transact again
  const handleTransactAgain = () => {
    if (onTransactAgain) {
      onTransactAgain();
    }
    onClose();
  };

  if (!transaction) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          >
            {/* Confetti effect */}
            {showConfetti && (
              <Confetti
                width={windowSize.width}
                height={windowSize.height}
                recycle={false}
                numberOfPieces={200}
                colors={['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#FFFFFF']}
              />
            )}

            {/* Modal */}
            <motion.div
              className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl overflow-hidden max-w-md w-full mx-4 shadow-2xl border-2 border-gray-700"
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{
                scale: 1,
                opacity: 1,
                y: 0,
                transition: {
                  type: 'spring',
                  damping: 15,
                  delay: 0.1,
                },
              }}
              exit={{ scale: 0.5, opacity: 0, y: 50 }}
              onClick={e => e.stopPropagation()}
            >
              {/* Animated border glow */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 opacity-20 animate-pulse"></div>

              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 z-50 bg-red-500 rounded-full p-1.5 text-white hover:bg-red-600 transition-colors shadow-lg"
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Modal content */}
              <div className="p-8 relative z-10">
                {/* Emoji animation */}
                <motion.div
                  className="text-center mb-4"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{
                    scale: 1,
                    rotate: 0,
                    transition: {
                      delay: 0.3,
                      type: 'spring',
                      stiffness: 200,
                    },
                  }}
                >
                  <span className="text-7xl">{getTransactionEmoji(transaction.type)}</span>
                </motion.div>

                {/* Celebration text */}
                <motion.div
                  className="text-center mb-6"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1, transition: { delay: 0.5 } }}
                >
                  <h2 className="text-white text-3xl font-bold mb-2">
                    {getCelebrationText(transaction.type)}
                  </h2>
                  <p className="text-gray-300 text-sm">Transaction processed successfully</p>
                </motion.div>

                {/* Transaction details */}
                <motion.div
                  className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    transition: {
                      delay: 0.7,
                      type: 'spring',
                    },
                  }}
                >
                  <div className="text-center">
                    <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 mb-2">
                      {transaction.optionText}
                    </p>
                    {transaction.amount && (
                      <p className="text-3xl font-bold text-green-400">
                        ${transaction.amount.toFixed(2)}
                      </p>
                    )}
                    {transaction.message && (
                      <p className="text-sm text-gray-400 mt-2">{transaction.message}</p>
                    )}
                  </div>
                </motion.div>

                {/* Action buttons */}
                <motion.div
                  className="flex flex-col space-y-3"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1, transition: { delay: 0.9 } }}
                >
                  <button
                    onClick={handleClose}
                    className="w-full py-3 px-4 rounded-lg font-bold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-green-500/30"
                  >
                    Done
                  </button>

                  {onViewReceipt && (
                    <button
                      onClick={handleViewReceipt}
                      className="w-full py-3 px-4 rounded-lg font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transition-all duration-300"
                    >
                      View Receipt
                    </button>
                  )}

                  {onTransactAgain && (
                    <button
                      onClick={handleTransactAgain}
                      className="w-full py-3 px-4 rounded-lg font-bold text-gray-300 bg-gray-700 hover:bg-gray-600 transition-all duration-300"
                    >
                      Make Another Transaction
                    </button>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AtmResultModal;
